import { z } from 'zod';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import { isValidPhoneNumber } from "react-phone-number-input";
import valid from "card-validator";

export const emailField = z.object({
  email: z.string().email("Email inválido").nonempty("Email obrigatório").trim(),
});

export const passwordSchema = z.object({
  password: z.string().nonempty("Senha obrigatória").trim(),
});

export const confirmPasswordSchema = z.object({
  confirmpassword: z.string().nonempty("Senha obrigatória").min(8, "Senha deve conter ao menos 8 dígitos").trim(),
});

export const token = z.object({
  token: z.string().nonempty("Token obrigatório").length(6, "O código deve ter 6 dígitos").trim(),
});

export const loginSchema = emailField.merge(passwordSchema);

export const forgotPasswordSchema = passwordSchema.merge(confirmPasswordSchema).merge(token);

export const UFs = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES",
  "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR",
  "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC",
  "SP", "SE", "TO"
];

export const nameField = z.object({
  Name: z
    .string()
    .min(2, "Nome deve ter ao menos 2 caracteres")
    .trim()
    .regex(/^[A-Za-zÀ-ÿ\s]+$/, "O nome deve conter apenas letras"),
});

export const cpfCnpjField = z.object({
  CpfCnpj: z.string().refine((val) => {
    const digits = val.replace(/\D/g, "");
    return cpf.isValid(digits) || cnpj.isValid(digits);
  }, {
    message: "CPF ou CNPJ inválido",
  }),
});

export const phoneField = z.object({
  PhoneNumber: z.string()
    .nonempty("Telefone é obrigatório")
    .refine(val => isValidPhoneNumber(val), {
      message: "Número de telefone inválido",
    })
});

export const addressFields = z.object({
  Address: z.string().trim().nonempty("Endereço é obrigatório").trim(),
  Zipcode: z
    .string()
    .nonempty("Cep é obrigatório").trim()
    .transform((val) => val.replace(/\D/g, ""))
    .refine((val) => /^\d{8}$/.test(val), {
      message: "CEP inválido. Deve conter exatamente 8 dígitos",
    }),
  Number: z
    .string()
    .nonempty("Número é obrigatório").trim()
    .transform((val) => val.replace(/\D/g, "")),
  Complement: z.string().optional(),
  District: z.string().nonempty("Bairro é obrigatório").trim(),
  City: z.string().nonempty("Cidade é obrigatória").trim(),
  State: z.enum(UFs, { errorMap: () => ({ message: "Estado inválido" }) }),
});


export const registerSchema = z.object({
  firstName: z.string().nonempty("Nome é obrigatório").trim(),
  lastName: z.string().nonempty("Sobrenome é obrigatório").trim(),
  phone: z.string()
    .nonempty("Telefone é obrigatório")
    .refine(val => isValidPhoneNumber(val), {
      message: "Número de telefone inválido",
    })
}).merge(emailField);


export const creditCardAddFormSchema = z.object({
  Label: z.string().nonempty("Apelido obrigatório").trim(),
  HolderName: z.string().nonempty("Nome do cartão obrigatório").trim(),

  CardNumber: z
    .string()
    .max(24, "Número muito longo")
    .refine((val) => valid.number(val).isValid, {
      message: "Número do cartão inválido",
    }),

  ExpirationDate: z
    .string()
    .min(5, "Data inválida")
    .refine((val) => {
      const [month, year] = val.split("/").map((v) => v.trim());
      if (!month || !year) return false;
      return valid.expirationDate({ month, year }).isValid;
    }, {
      message: "Data de validade inválida",
    }),

  Cvv: z
    .string()
    .min(3, "CCV muito curto")
    .max(5, "CVV muito longo")
})
  .merge(cpfCnpjField)
  .merge(addressFields)